// Captions, written from the post itself in three voices, until Bailey writes
// them (docs/SOCIAL_BACKEND.md). Five hashtags at most, one of them local:
// Instagram now shows posts by what they say, not by piles of tags.
import { postDay, postStylist } from './dates'

export const VOICES = [
  { id: 'warm', label: 'Warm' },
  { id: 'short', label: 'Short' },
  { id: 'fun', label: 'Playful' },
]

const plain = (s) => String(s || '').replace(/\*/g, '').replace(/\s*\n\s*/g, ' ').trim()
const low = (s) => plain(s).toLowerCase()
const pick = (voice, warm, short, fun) => (voice === 'short' ? short : voice === 'fun' ? fun : warm)
// Ends a sentence once, whatever the words already end with
const end = (s) => { const t = plain(s); return !t ? '' : /[.!?…]$/.test(t) ? t : `${t}.` }
// A headline set in capitals on the post reads as a sentence in the caption
const calm = (s) => { const t = plain(s); return t && t === t.toUpperCase() && /[A-Z]{3}/.test(t) ? t.charAt(0) + t.slice(1).toLowerCase() : t }
const listOf = (list) => (Array.isArray(list) ? list : []).map(x => plain(x)).filter(Boolean)
const numbered = (list) => listOf(list).map((x, i) => `${i + 1}. ${end(x)}`).join('\n')

// "12 High Street, Bristol BS1 4DJ" -> "Bristol"
export const townOf = (address) => {
  const parts = String(address || '').split(',').map(s => s.trim()).filter(Boolean)
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const words = parts[i].replace(/\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i, '').replace(/\b(uk|united kingdom|england|scotland|wales)\b/i, '').trim()
    if (words && !/\d/.test(words) && words.length <= 24) return words
  }
  return ''
}
const tag = (s) => `#${String(s || '').replace(/[^A-Za-z0-9]/g, '')}`

const TAGS = {
  hair: { base: ['hair', 'hairsalon'], beforeAfter: 'hairtransformation', freshWork: 'hairinspo', offer: 'hairoffers', review: 'happyclients', team: 'hairstylist', priceList: 'hairprices', giftVouchers: 'giftvouchers', tips: 'haircare', question: 'hairadvice', freeSlots: 'hairappointment', oneSlot: 'lastminute', newService: 'newservice', local: 'hair' },
  barber: { base: ['barber', 'barbershop'], beforeAfter: 'freshcut', freshWork: 'skinfade', offer: 'barberdeals', review: 'barberlife', team: 'barberlife', priceList: 'barberprices', giftVouchers: 'giftvouchers', tips: 'mensgrooming', question: 'mensgrooming', freeSlots: 'barberappointment', oneSlot: 'lastminute', newService: 'newservice', local: 'barber' },
  beauty: { base: ['beauty', 'beautysalon'], beforeAfter: 'beautytransformation', freshWork: 'nailinspo', offer: 'beautydeals', review: 'happyclients', team: 'beautytherapist', priceList: 'beautyprices', giftVouchers: 'giftvouchers', tips: 'beautytips', question: 'beautyadvice', freeSlots: 'beautyappointment', oneSlot: 'lastminute', newService: 'newtreatment', local: 'beauty' },
}

export const hashtagsFor = (post, town) => {
  const t = TAGS[post.trade] || TAGS.hair
  const list = [t[post.type], ...t.base]
  if (town) list.unshift(`${town.toLowerCase()}${t.local}`)
  return Array.from(new Set(list.filter(Boolean))).slice(0, 5).map(tag)
}

// More tags worth suggesting, by trade (the Caption tool shows the ones not
// already on the post)
const MORE_TAGS = {
  hair: ['haircolour', 'balayage', 'haircut', 'newhair', 'salonlife', 'hairgoals', 'blowdry', 'ukhair'],
  barber: ['fade', 'freshcut', 'menshair', 'beard', 'taperfade', 'barbering', 'haircut', 'ukbarber'],
  beauty: ['nails', 'nailart', 'lashes', 'brows', 'biab', 'skincare', 'selfcare', 'ukbeauty'],
}
export const suggestedTags = (post, town) => {
  const t = TAGS[post.trade] || TAGS.hair
  const place = town ? String(town).toLowerCase().replace(/[^a-z0-9]/g, '') : ''
  const local = place ? [`${place}${t.local}`, `${place}${post.trade === 'barber' ? 'barbers' : 'salon'}`, place] : []
  return Array.from(new Set([...hashtagsFor(post, town).map(x => x.slice(1)), ...local, ...(MORE_TAGS[post.trade] || MORE_TAGS.hair)])).map(tag)
}

// "Tomorrow" reads as "tomorrow" mid-sentence; a weekday gets "on"
const onDay = (day) => (!day ? '' : /^(today|tomorrow)$/i.test(day) ? day.toLowerCase() : `on ${day}`)

const body = (post, voice, salon) => {
  const c = post.content || {}
  const day = postDay(post)
  switch (post.type) {
    case 'freeSlots': {
      const times = (c.times || []).filter(Boolean).join(', ')
      const more = c.more ? ' More times too.' : ''
      // Only the booking layouts ask what the times are for
      const what = post.family === 'layouts' && plain(c.service) ? ` for ${low(c.service)}` : ''
      // One stylist's free times, with their name on
      const who = postStylist(post)
      if (who) {
        return pick(voice,
          `${who} has a few spaces free${what} ${onDay(day)}: ${times}.${more}\n\nFirst come, first served. Ask for ${who} when you book. ${end(c.cta)}`,
          `${day} with ${who}: ${times}. ${end(c.cta)}`,
          `Plot twist: ${who} has time free ${onDay(day)} 🙌 ${times}. Grab one before they go.`)
      }
      return pick(voice,
        `We’ve had a few spaces open up${what} ${onDay(day)}: ${times}.${more}\n\nFirst come, first served. ${end(c.cta)}`,
        `${day}: ${times}. ${end(c.cta)}`,
        `Plot twist: we’ve got chairs free ${onDay(day)} 🙌 ${times}. Grab one before they go.`)
    }
    case 'oneSlot': {
      const who = postStylist(post)
      if (who) {
        return pick(voice,
          `A space has just opened up with ${who} ${onDay(day)} at ${plain(c.time)}${c.service ? `, perfect for ${low(c.service)}` : ''}. ${end(c.line)}\n\n${end(c.cta)}`,
          `${day} with ${who}, ${plain(c.time)}. ${end(c.service)} ${end(c.cta)}`,
          `Someone’s plans changed, and yours just got better 👀 ${day} at ${plain(c.time)} with ${who}. First to book gets it.`)
      }
      return pick(voice,
        `A space has just opened up ${onDay(day)} at ${plain(c.time)}${c.service ? `, perfect for ${low(c.service)}` : ''}. ${end(c.line)}\n\n${end(c.cta)}`,
        `${day}, ${plain(c.time)}. ${end(c.service)} ${end(c.cta)}`,
        `Someone’s plans changed, and yours just got better 👀 ${day} at ${plain(c.time)}. First to book gets it.`)
    }
    case 'freshWork':
      return pick(voice,
        `${end(c.headline)} ${end(c.detail)}\n\n${/\?/.test(c.cta || '') ? end(c.cta) : `Want yours? ${end(c.cta)}`}`,
        `${end(c.headline)} ${plain(c.detail)}`,
        `Fresh out the chair and feeling it ✨ ${end(c.headline)} ${plain(c.detail)}`)
    case 'beforeAfter':
      return pick(voice,
        `${end(c.headline)} ${end(c.detail)}\n\n${/\?/.test(c.cta || '') ? end(c.cta) : `Love this one? ${end(c.cta)}`}`,
        `${end(c.headline)} ${plain(c.detail)}`,
        `Before, after, and a very happy client 🙌 ${end(c.detail)}`)
    case 'review':
      return pick(voice,
        `Messages like this make our week. Thank you, ${plain(c.name)} 🤍\n\n“${plain(c.quote)}”`,
        `“${plain(c.quote)}” ${plain(c.name)}`,
        `Not us screenshotting this straight away 🥹 Thank you, ${plain(c.name)}!\n\n“${plain(c.quote)}”`)
    case 'team':
      return pick(voice,
        `Meet ${plain(c.name)}, our ${low(c.role)}. ${end(c.line)}\n\nBook in with ${plain(c.name)} through the link in our bio.`,
        `Meet ${plain(c.name)}. ${end(c.role)}`,
        `Say hi to ${plain(c.name)} 👋 ${end(c.line)}`)
    case 'priceList':
      return pick(voice,
        `Our prices, all in one place. ${end(c.note)} ${end(c.cta)}`,
        `Our prices. ${end(c.cta)}`,
        `Save this one for later 📌 Everything we do, and what it costs.`)
    case 'tips':
      if (post.family === 'layouts' && (post.layout === 'steps' || post.layout === 'checklist') && listOf(c.steps).length) {
        return pick(voice,
          `${end(calm(c.headline))}\n\n${numbered(c.steps)}\n\nSave this for later.`,
          `${end(calm(c.headline))} ${listOf(c.steps).map(end).join(' ')}`,
          `Save this, thank us later 😉 ${end(calm(c.headline))}\n\n${numbered(c.steps)}`)
      }
      return pick(voice,
        `${end(calm(c.headline))} ${end(c.body)}\n\nSave this for later.`,
        `${end(calm(c.headline))} ${end(c.body)}`,
        `Save this, thank us later 😉 ${end(calm(c.headline))} ${end(c.body)}`)
    case 'question': {
      const thought = post.family === 'layouts' && post.layout === 'myth' && plain(c.thought) ? `“${plain(c.thought)}” We hear that a lot. ` : ''
      return pick(voice,
        `${plain(c.headline)} ${thought}${end(c.body)}\n\n${end(c.cta)}`,
        `${plain(c.headline)} ${end(c.cta)}`,
        `We get asked this a lot 👇 ${plain(c.headline)} ${thought}${end(c.body)}`)
    }
    case 'newService': {
      const what = end(calm(c.headline))
      const incl = post.family === 'layouts' && post.layout === 'included' && listOf(c.items).length ? `\n\nWhat’s included:\n${listOf(c.items).map(x => `• ${x}`).join('\n')}` : ''
      return pick(voice,
        `New on our menu: ${what} ${end(c.body)}${incl}${c.detail ? `\n\n${end(c.detail)}` : ''}\n\n${end(c.cta)}`,
        `New: ${what} ${end(c.cta)}`,
        `Something new just landed ✨ ${what} ${end(c.body)}${incl}\n\n${end(c.cta)}`)
    }
    case 'offer': {
      // A live discount code: how to use it and its small print, in full
      // (CAP Code 8.17: the conditions go with the offer)
      if (plain(c.code)) {
        const use = `Use ${plain(c.code)} when you book.`
        const small = plain(c.small) ? `\n\n${end(c.small)}` : ''
        const price = plain(c.price) ? ` ${end(c.price)}` : ''
        return pick(voice,
          `${plain(c.value)} ${plain(c.service)}.${price} ${use}\n\n${end(c.cta)}${small}`,
          `${plain(c.value)} ${plain(c.service)}. ${use}${small}`,
          `Treat yourself, it’s basically a rule 💸 ${plain(c.value)} ${plain(c.service)}. ${use}${small}`)
      }
      return pick(voice,
        `${plain(c.value)} ${plain(c.service)}. ${plain(c.price)}${c.dates ? `, ${low(c.dates)}` : ''}.\n\n${end(c.cta)}`,
        `${plain(c.value)} ${plain(c.service)}. ${end(c.price)}`,
        `Treat yourself, it’s basically a rule 💸 ${plain(c.value)} ${plain(c.service)}. ${end(c.price)}`)
    }
    case 'giftVouchers':
      // No price when the salon's vouchers have none set
      if (!plain(c.from)) {
        return pick(voice,
          `${end(c.headline)} Gift vouchers, bought online and sent by email.\n\n${end(c.cta)}`,
          `Gift vouchers, bought online. Link in bio.`,
          `Stuck for a gift? Sorted 🎁 Vouchers, bought in a minute.`)
      }
      return pick(voice,
        `${end(c.headline)} Gift vouchers ${low(c.from)}.\n\n${end(c.cta)}`,
        `Gift vouchers ${low(c.from)}. Link in bio.`,
        `Stuck for a gift? Sorted 🎁 Vouchers ${low(c.from)}, bought in a minute.`)
    case 'announce':
      return pick(voice,
        `${end(calm(c.headline))} ${end(c.body)}\n\n${end(c.cta)}`,
        `${end(calm(c.headline))} ${end(c.cta)}`,
        `${end(calm(c.headline))} ✨ ${end(c.body)}\n\n${end(c.cta)}`)
    default:
      return `From ${salon || 'us'}.`
  }
}

// Words a screen reader says for the image
export const altTextFor = (post, salon) => {
  const c = post.content || {}
  const from = salon ? ` at ${salon}` : ''
  const day = postDay(post)
  switch (post.type) {
    case 'freeSlots': return `Free appointments${postStylist(post) ? ` with ${postStylist(post)}` : ''}${post.family === 'layouts' && plain(c.service) ? ` for ${low(c.service)}` : ''}${from} ${day}: ${(c.times || []).filter(Boolean).join(', ')}.`
    case 'oneSlot': return `One free appointment${postStylist(post) ? ` with ${postStylist(post)}` : ''}${from}: ${day} at ${plain(c.time)}${c.service ? `, ${low(c.service)}` : ''}.`
    case 'freshWork': return `A photo of recent work${from}: ${end(c.headline)} ${plain(c.detail)}`.trim()
    case 'beforeAfter': return `Before and after photos${from}: ${plain(c.detail) || plain(c.headline)}.`
    case 'review': return `A ${c.rating || 5} star review from ${plain(c.name)}: ${plain(c.quote)}`
    case 'team': return `A photo of ${plain(c.name)}, ${plain(c.role)}${from}.`
    case 'priceList': return `Price list${from}: ${(c.items || []).filter(i => i && i.name).map(i => `${i.name} ${i.price}`).join(', ')}.`
    case 'tips': return post.family === 'layouts' && (post.layout === 'steps' || post.layout === 'checklist') ? `${end(calm(c.headline))} ${listOf(c.steps).map(end).join(' ')}` : `A tip${from}: ${end(calm(c.headline))} ${end(c.body)}`
    case 'newService': return `A new service${from}: ${end(calm(c.headline))} ${end(c.body)}`.trim()
    case 'question': return `${plain(c.headline)} ${end(c.body)}`
    case 'offer': return `An offer${from}: ${plain(c.value)} ${plain(c.service)}. ${end(c.price)}${plain(c.code) ? ` Code ${plain(c.code)}.` : ''}`.trim()
    case 'giftVouchers': return plain(c.from) ? `Gift vouchers${from}, ${low(c.from)}.` : `Gift vouchers${from}.`
    case 'announce': return `${plain(c.eyebrow) ? `${plain(c.eyebrow)}${from}: ` : ''}${end(calm(c.headline))} ${end(c.body)}`.trim()
    default: return `A post${from}.`
  }
}

// { text, hashtags, alt, title } for a post in a voice
export const buildCaption = (post, { voice = 'warm', salon = '', town = '' } = {}) => {
  const text = body(post, voice, salon).replace(/[ \t]+\n/g, '\n').replace(/ {2,}/g, ' ').replace(/\.\./g, '.').trim()
  const hashtags = hashtagsFor(post, town)
  const c = post.content || {}
  const title = calm(c.headline || c.title || c.value || c.name) || 'New post'
  return { voice, text, hashtags, alt: altTextFor(post, salon), title: title.slice(0, 90) }
}

// What gets copied: the words, then the tags on their own line
export const captionText = (caption) => (caption ? [caption.text, (caption.hashtags || []).join(' ')].filter(Boolean).join('\n\n') : '')
